import React, { useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import { scrollToTop, knox_hero1, knox_hero2, knox_hero1_mob, knox_hero2_mob, knox_logo } from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import HomePageFooter from "src/views/home/components/Footer";
import Footer from "src/components/footer";

const KnoxHR: React.FC = () => {

  useEffect(() => {
    scrollToTop();
  }, []);

  const everythingHRCards = [
    {
      num: "1.",
      heading: "Employee Information Management:",
      body: "Maintain the employee details that power everyday HR operations in a central system of record. Help authorized teams find the information they need while giving employees a consistent portal experience.",
    },
    {
      num: "2.",
      heading: "Attendance and Leave Management:",
      body: "Track attendance and leave activity, review and action eligible requests, and manage leave balances. Configure leave types, holidays, and work schedules to reflect how your organisation works.",
    },
    {
      num: "3.",
      heading: "Reimbursements:",
      body: "Give employees a clear way to submit reimbursement requests and attach supporting files. Employees can follow their requests, while authorized users can review the information needed to process them.",
    },
    {
      num: "4.",
      heading: "Performance and Appraisals:",
      body: "Support structured performance conversations with KPI reviews and staged appraisal forms. Employees and assigned reviewers can complete the relevant steps in sequence, creating a clear process from response to acknowledgement.",
    },
    {
      num: "5.",
      heading: "Organisation Policies and Documents:",
      body: "Create a dependable home for company policies. Administrators can publish and manage policy documents, and employees can search, view, and download the policies available to them.",
    },
    {
      num: "6.",
      heading: "Role-Based Access:",
      body: "Access is shaped by the permissions assigned to each user. This helps employees, managers, HR teams, and administrators work with the actions and information appropriate to their responsibilities.",
    },
  ];

  const processSteps = [
    {
      stepNum: "1.",
      spineLabel: "SELF-SERVICE",
      title: "Employee Self-Service at the Centre",
      body: "Give employees a clear and intuitive way to access the HR services relevant to them.",
      theme: "light",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      stepNum: "2.",
      spineLabel: "WORKFLOWS",
      title: "Structured Processes",
      body: "Manage attendance, leave, reimbursements, performance, and policy workflows with greater consistency.",
      theme: "dark",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      ),
    },
    {
      stepNum: "3.",
      spineLabel: "SETTINGS",
      title: "Configurable for Your Organization",
      body: "Configure leave settings, holidays, document visibility, and access based on your operational requirements.",
      theme: "light",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
    },
    {
      stepNum: "4.",
      spineLabel: "ACCESS",
      title: "Built Around Permissions",
      body: "Ensure users can view and perform actions based on their authorised access.",
      theme: "dark",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
    },
  ];

  const comparisons = [
    {
      num: "1",
      before: "Employee information and HR records were scattered in tools and folders.",
      knoxhr: "A single HR portal for essential day-to-day operations.",
    },
    {
      num: "2",
      before: "Leave balances and policy documents are difficult to track.",
      knoxhr: "Central administration and employee access for supported HR processes.",
    },
    {
      num: "3",
      before: "Reimbursement requests rely on manual follow-ups.",
      knoxhr: "Employees can submit requests and supporting documents digitally.",
    },
    {
      num: "4",
      before: "Appraisals depend on disconnected forms and reminders.",
      knoxhr: "Structured HR-managed performance review workflows.",
    },
    {
      num: "5",
      before: "Employees do not have a clear view of HR information.",
      knoxhr: "A shared self-service experience.",
    },
  ];

  return (
    <div className="knoxhr-container animate__animated animate__fadeIn">
      {/* 1. Hero Section */}
      <section className="knoxhr-hero">
        <div className="hero-backdrop-pillar" />
        <div className="hero-content-left animate__animated animate__fadeInLeft">
          <h1 className="hero-title">KnoxHR</h1>

          <p className="hero-subtitle">
            Knox HR is a unified HR platform for managing employee data, attendance,
            leave, reimbursements, performance, and documents—giving employees
            self-service and HR greater control.
          </p>
        </div>

        {/* Right Side KnoxHR Official Logo */}
        <div className="hero-chevron-right animate__animated animate__fadeInRight">
          <img src={knox_logo} alt="KnoxHR" className="knoxhr-hero-logo" />
        </div>

        {/* Breadcrumb Navigation */}
        <div className="hero-breadcrumb">
          <Link to={pageRoutes.Home} onClick={scrollToTop}>
            Home
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={pageRoutes.Marketplace} onClick={scrollToTop}>
            Marketplace
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-active">KnoxHR</span>
        </div>
      </section>

      {/* 2. "What is KnoxHR?" Section */}
      <section className="what-is-knoxhr-section">
        <div className="what-is-container">
          <div className="what-is-text animate__animated animate__fadeInUp">
            <div className="what-is-header-row">
              <div className="title-grey-box" aria-hidden="true" />
              <h2 className="outline-title">
                What is
                <br />
                <span className="knoxhr-outline-row">
                  KnoxHR?
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>

            {/* In mobile, mockup is displayed between title and description */}
            <div className="what-is-mockup-mobile">
              <div className="knox-layered-mockup">
                <object data={knox_hero1_mob} type="image/svg+xml" aria-label="KnoxHR Employee Dashboard" className="knox-mockup-bg" style={{ pointerEvents: 'none' }} />
                <object data={knox_hero2_mob} type="image/svg+xml" aria-label="KnoxHR Feature Overview" className="knox-mockup-fg" style={{ pointerEvents: 'none' }} />
              </div>
            </div>

            <p className="what-is-p">
              HR teams should not have to piece together employee records, requests,
              approvals, and policies across disconnected tools.{" "}
              <a
                href="https://www.knoxhr.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="highlight-brand"
              >
                <strong className="highlight-brand">KnoxHR</strong>
              </a>{" "}
              brings these
              everyday workflows into one workspace, helping teams spend less time
              chasing information and more time supporting people.
            </p>

            <p className="what-is-p">
              Employees can access the HR services relevant to them, while
              authorized HR and administrators can manage records, review requests,
              maintain leave settings, and oversee organizational processes from a
              central portal.
            </p>
          </div>

          <div className="what-is-mockup animate__animated animate__fadeInRight">
            <div className="knox-layered-mockup">
              <img src={knox_hero1} alt="KnoxHR Employee Dashboard" className="knox-mockup-bg" />
              <img src={knox_hero2} alt="KnoxHR Feature Overview" className="knox-mockup-fg" />
            </div>
          </div>
        </div>
      </section>

      {/* 3. "Everything HR Needs, In One Place" Section */}
      <section className="everything-hr-section">
        <div className="everything-header">
          <h2 className="everything-title">
            Everything HR Needs, In{" "}
            <span className="highlight-blue">One Place</span>
          </h2>
          <p className="everything-desc">
            From employee management to attendance, leave, performance, and more—simplified in one platform.
          </p>
        </div>

        <div className="hr-cards-grid">
          {everythingHRCards.map((card, idx) => (
            <div className="hr-feature-card" key={idx}>
              <div className="card-num">{card.num}</div>
              <h3 className="card-heading">{card.heading}</h3>
              <p className="card-body">{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4. "Designed Around Your Organization and the Way You Work" Section */}
      <section className="designed-work-section">
        <div className="designed-header">
          <h2 className="designed-title">
            Designed Around Your Organization and the{" "}
            <span className="highlight-blue">Way You Work</span>
          </h2>
        </div>

        {/* Stepped Process Layout for Desktop & Mobile */}
        <div className="stepped-process-container">
          {/* Decorative Stepped Dashed SVG Line */}
          <svg
            className="stepped-connecting-line"
            viewBox="0 0 1060 766"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M 460 77 H 672 Q 688 77 688 93 V 204"
              stroke="#1777A6"
              strokeWidth="2"
              strokeDasharray="5 5"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d="M 600 281 H 388 Q 372 281 372 297 V 408"
              stroke="#1777A6"
              strokeWidth="2"
              strokeDasharray="5 5"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d="M 460 485 H 672 Q 688 485 688 501 V 612"
              stroke="#1777A6"
              strokeWidth="2"
              strokeDasharray="5 5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          <div className="stepped-grid">
            {processSteps.map((step, idx) => (
              <div
                className={`stepped-row ${idx % 2 === 0 ? "row-left" : "row-right"}`}
                key={idx}
              >
                <div className={`process-card theme-${step.theme}`}>
                  <div className="card-spine">{step.spineLabel}</div>
                  <div className="card-content">
                    <div className="step-header">
                      <span className="step-icon">{step.icon}</span>
                      <h3 className="step-title">
                        {step.stepNum} {step.title}
                      </h3>
                    </div>
                    <p className="step-desc">{step.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. "HR, Without the Complexity" (Comparison Table) */}
      <section className="without-complexity-section">
        <div className="complexity-header">
          <h2 className="complexity-title">
            HR, Without the <span className="highlight-blue">Complexity</span>
          </h2>
        </div>

        {/* Desktop Side-by-Side Comparison Table */}
        <div className="comparison-table-desktop">
          <div className="table-header-row">
            <div className="th-col th-before">Before</div>
            <div className="th-col th-knoxhr">With KnoxHR</div>
          </div>

          <div className="table-body-rows">
            {comparisons.map((item, idx) => (
              <div className="table-row" key={idx}>
                <div className="td-col td-before">
                  <span className="row-number">{item.num}</span>
                  <span>{item.before}</span>
                </div>
                <div className="td-col td-knoxhr">
                  <span>{item.knoxhr}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Stacked Comparison Cards (Screenshot 2 Match) */}
        <div className="comparison-mobile-stacked">
          {/* Before Card */}
          <div className="compare-card card-before">
            <div className="card-header-bar">Before</div>
            <div className="card-items-list">
              {comparisons.map((item, idx) => (
                <div className="list-item" key={idx}>
                  <span className="item-num">{item.num}</span>
                  <span>{item.before}</span>
                </div>
              ))}
            </div>
          </div>

          {/* With KnoxHR Card */}
          <div className="compare-card card-knoxhr">
            <div className="card-header-bar">With KnoxHR</div>
            <div className="card-items-list">
              {comparisons.map((item, idx) => (
                <div className="list-item" key={idx}>
                  <span className="item-num">{item.num}</span>
                  <span>{item.knoxhr}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. Closing Callout CTA */}
      <section className="create-experience-cta">
        <div className="cta-content-wrap">
          <div className="cta-left">
            <h2 className="cta-headline">
              Create a <span className="highlight-blue">simpler</span>,
              <br />
              <span className="highlight-blue">more connected</span> HR experience.
            </h2>
          </div>

          <div className="cta-right">
            <p className="cta-desc">
              Bring the everyday work of HR into one place with Knox HR. Give your people
              easier access to the services they need and give your team a stronger
              foundation for managing the work behind them.
            </p>

            <a
              href="https://www.knoxhr.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-action-btn"
            >
              Book a demo
            </a>
          </div>
        </div>
      </section>

      {/* 7. Pre-Footer Contact Banner */}
      <HomePageFooter page="KnoxHR" />

      {/* 8. Main Website Footer */}
      <Footer />
    </div>
  );
};

export default KnoxHR;
